/**
 * App shell + tiny router. A valuer's whole experience: sign in -> My uploads -> New
 * upload. Two screens, switched by local state (no route library — this is deliberately
 * small). Reviewers can sign in but are gently told the dashboard comes later.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './auth';
import { fetchProfile, ApiError, type Profile } from './api';
import { missingEnv } from './env';
import { Header } from './components/Header';
import { Tile, Spinner, Banner, Button } from './components/ui';
import { Login } from './screens/Login';
import { MyUploads } from './screens/MyUploads';
import { NewUpload } from './screens/NewUpload';
import { FeedbackScreen } from './screens/valuer/FeedbackScreen';
import { ReviewerApp } from './screens/reviewer/ReviewerApp';

/** Phase 7: a "changes requested" email links straight to #/feedback/<reportId>. Parsed once at
 * mount, same lightweight approach as the rest of the valuer app (no route library). */
function feedbackReportIdFromHash(): string | null {
  const match = /^#\/feedback\/([^/?#]+)/.exec(window.location.hash);
  return match ? match[1]! : null;
}

type ValuerView = { name: 'list' } | { name: 'new' } | { name: 'feedback'; reportId: string; address: string | null };

function Splash({ label }: { label: string }) {
  return (
    <div className="login-wrap">
      <Spinner label={label} />
    </div>
  );
}

function ConfigError() {
  return (
    <div className="login-wrap">
      <Tile className="login-card tile--pad-lg">
        <h1 className="login-title">Almost there</h1>
        <p className="login-sub">This app is missing some configuration:</p>
        <Banner tone="error">Missing: {missingEnv.join(', ')}</Banner>
        <p className="field__hint" style={{ marginTop: 16 }}>
          Set these public values in the build environment (see .env.example) and redeploy.
        </p>
      </Tile>
    </div>
  );
}

export function App() {
  const { session, loading, signOut } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [view, setView] = useState<ValuerView>(() => {
    const reportId = feedbackReportIdFromHash();
    return reportId ? { name: 'feedback', reportId, address: null } : { name: 'list' };
  });

  const loadProfile = useCallback(() => {
    setProfileError(null);
    fetchProfile()
      .then(setProfile)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          void signOut();
        } else {
          setProfileError(err instanceof Error ? err.message : 'Could not load your account.');
        }
      });
  }, [signOut]);

  useEffect(() => {
    if (!session) {
      setProfile(null);
      setProfileError(null);
      setView({ name: 'list' });
      return;
    }
    loadProfile();
  }, [session, loadProfile]);

  if (missingEnv.length > 0) return <ConfigError />;
  if (loading) return <Splash label="Loading…" />;
  if (!session) return <Login />;

  const userName = profile?.displayName ?? profile?.email ?? null;

  const shell = (children: ReactNode) => (
    <div className="app">
      <Header
        userName={userName}
        onSignOut={() => void signOut()}
        showAdminLink={profile?.role === 'reviewer' && profile.isAdmin}
        onOpenAdmin={() => {
          window.location.hash = '#/admin/checks';
        }}
      />
      {children}
    </div>
  );

  if (profileError) {
    return shell(
      <main className="main">
        <div className="stack">
          <Banner tone="error">{profileError}</Banner>
          <div>
            <Button variant="secondary" onClick={loadProfile}>
              Try again
            </Button>
          </div>
        </div>
      </main>,
    );
  }

  if (!profile) return shell(<Splash label="Loading your account…" />);

  if (!profile.isValuer) {
    if (profile.role === 'reviewer') {
      return shell(<ReviewerApp isAdmin={profile.isAdmin} />);
    }
    return shell(
      <main className="main">
        <Tile className="tile--pad-lg">
          <div className="empty">
            <div className="empty__title">Account not set up for uploads</div>
            <p className="empty__body">
              Your account isn’t set up to upload yet. Please contact the reviewer to be added.
            </p>
          </div>
        </Tile>
      </main>,
    );
  }

  if (view.name === 'feedback') {
    return shell(
      <FeedbackScreen
        reportId={view.reportId}
        address={view.address}
        onBack={() => setView({ name: 'list' })}
        onResubmitted={() => setView({ name: 'list' })}
      />,
    );
  }

  return shell(
    view.name === 'list' ? (
      <MyUploads
        onNew={() => setView({ name: 'new' })}
        onViewFeedback={(reportId, address) => setView({ name: 'feedback', reportId, address })}
      />
    ) : (
      <NewUpload onList={() => setView({ name: 'list' })} onSessionExpired={() => void signOut()} />
    ),
  );
}
